/**
 * useDependencies Hook
 * Gestiona las dependencias entre tareas del roadmap
 * Similar a las dependencias de issues en GitHub
 */

import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'fixarr-task-dependencies-v1';

export const useDependencies = () => {
  const [dependencies, setDependencies] = useState({});

  // Cargar dependencias de localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setDependencies(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading dependencies:', error);
    }
  }, []);

  // Guardar en localStorage
  const saveDependencies = useCallback((deps) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(deps));
    } catch (error) {
      console.error('Error saving dependencies:', error);
    }
  }, []);

  /**
   * Añadir una dependencia a una tarea
   * @param {string} taskId - ID de la tarea
   * @param {string} dependsOnId - ID de la tarea de la que depende
   */
  const addDependency = useCallback((taskId, dependsOnId) => {
    // No permitir que una tarea dependa de sí misma
    if (taskId === dependsOnId) return false;

    setDependencies(prev => {
      const taskDeps = prev[taskId] || [];
      
      // Evitar duplicados
      if (taskDeps.includes(dependsOnId)) return prev;
      
      const newDeps = {
        ...prev,
        [taskId]: [...taskDeps, dependsOnId]
      };
      
      saveDependencies(newDeps);
      return newDeps;
    });
    
    return true;
  }, [saveDependencies]);

  /**
   * Eliminar una dependencia de una tarea
   */
  const removeDependency = useCallback((taskId, dependsOnId) => {
    setDependencies(prev => {
      const taskDeps = prev[taskId] || [];
      const newDeps = {
        ...prev,
        [taskId]: taskDeps.filter(id => id !== dependsOnId)
      };
      
      // Limpiar si no quedan dependencias
      if (newDeps[taskId].length === 0) {
        delete newDeps[taskId];
      }
      
      saveDependencies(newDeps);
      return newDeps;
    });
  }, [saveDependencies]);

  /**
   * Obtener las dependencias de una tarea
   */
  const getDependencies = useCallback((taskId) => {
    return dependencies[taskId] || [];
  }, [dependencies]);

  /**
   * Obtener las tareas que dependen de una tarea específica
   * (dependencias inversas - quién depende de mí)
   */
  const getDependents = useCallback((taskId) => {
    const dependents = [];
    Object.entries(dependencies).forEach(([id, deps]) => {
      if (deps.includes(taskId)) {
        dependents.push(id);
      }
    });
    return dependents;
  }, [dependencies]);

  /**
   * Verificar si una tarea está bloqueada
   * Una tarea está bloqueada si alguna de sus dependencias no está completada
   */
  const isTaskBlocked = useCallback((taskId, tasks) => {
    const taskDeps = dependencies[taskId] || [];
    
    if (taskDeps.length === 0) return { blocked: false, blockedBy: [] };
    
    const blockedBy = [];
    
    taskDeps.forEach(depId => {
      // Buscar la tarea en todas las fases
      const depTask = tasks.find(t => t.id === depId);
      if (depTask && depTask.status !== 'completed') {
        blockedBy.push(depTask);
      }
    });
    
    return {
      blocked: blockedBy.length > 0,
      blockedBy
    };
  }, [dependencies]);

  /**
   * Verificar si añadir una dependencia crearía un ciclo
   */
  const wouldCreateCycle = useCallback((taskId, dependsOnId) => {
    // DFS para detectar ciclos
    const visited = new Set();
    const stack = [dependsOnId];
    
    while (stack.length > 0) {
      const current = stack.pop();
      
      if (current === taskId) return true;
      
      if (!visited.has(current)) {
        visited.add(current);
        const deps = dependencies[current] || [];
        stack.push(...deps);
      }
    }
    
    return false;
  }, [dependencies]);

  /**
   * Añadir dependencia con verificación de ciclos
   */
  const addDependencySafe = useCallback((taskId, dependsOnId) => {
    if (taskId === dependsOnId) {
      return { success: false, error: 'Una tarea no puede depender de sí misma' };
    }
    
    if (wouldCreateCycle(taskId, dependsOnId)) {
      return { success: false, error: 'Esto crearía una dependencia circular' };
    }
    
    addDependency(taskId, dependsOnId);
    return { success: true };
  }, [addDependency, wouldCreateCycle]);

  /**
   * Obtener todas las tareas disponibles como dependencias para una tarea
   * (excluye la propia tarea y las que crearían ciclos)
   */
  const getAvailableDependencies = useCallback((taskId, allTasks) => {
    return allTasks.filter(task => {
      if (task.id === taskId) return false;
      if (wouldCreateCycle(taskId, task.id)) return false;
      return true;
    });
  }, [wouldCreateCycle]);

  /**
   * Obtener estadísticas de dependencias
   */
  const getStats = useCallback(() => {
    const totalTasks = Object.keys(dependencies).length;
    let totalDependencies = 0;
    
    Object.values(dependencies).forEach(deps => {
      totalDependencies += deps.length;
    });
    
    return {
      tasksWithDependencies: totalTasks,
      totalDependencies
    };
  }, [dependencies]);

  /**
   * Limpiar todas las dependencias
   */
  const clearAll = useCallback(() => {
    setDependencies({});
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return {
    dependencies,
    addDependency: addDependencySafe,
    removeDependency,
    getDependencies,
    getDependents,
    isTaskBlocked,
    getAvailableDependencies,
    getStats,
    clearAll
  };
};

export default useDependencies;
